/**
 * Meter Values Grid Component
 * Displays meter values for a session with pagination
 * Matches Flutter's meter_values_details_grid.dart
 */

import { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
// Note: Using raw HTML table elements instead of shadcn Table
// because shadcn Table has nested overflow-auto div that breaks sticky header
import { Skeleton } from '@/core/components/ui/skeleton';
import { MeterValueGridRow } from '../types/meter-value-types';
import { cn } from '@/core/lib/utils';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';

interface MeterValuesGridProps {
  data: MeterValueGridRow[];
  isLoading: boolean;
  error?: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onRefresh?: () => void;
  /** In 'A' mode the Current column is placed before Power. Defaults to kW. */
  unitMode?: 'A' | 'kW';
}

/**
 * Column definitions for meter values grid
 * Matches Flutter's ChargingMeterValueDetailsGrid columns
 */
const columns: ColumnDef<MeterValueGridRow>[] = [
  {
    accessorKey: 'meterValueId',
    header: 'Meter Value ID',
    size: 120,
    meta: { align: 'left' },
  },
  {
    accessorKey: 'sessionId',
    header: 'Session ID',
    size: 100,
    meta: { align: 'left' },
  },
  {
    accessorKey: 'timestamp',
    header: 'Timestamp',
    size: 190,
    meta: { align: 'left' },
  },
  {
    accessorKey: 'readingContext',
    header: 'Reading Context',
    size: 140,
    meta: { align: 'left' },
  },
  {
    accessorKey: 'energy',
    header: 'Energy (kWh)',
    size: 120,
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return value !== null ? value.toFixed(3) : '--';
    },
    meta: { align: 'right' },
  },
  {
    accessorKey: 'deltaEnergy',
    header: 'Delta Energy',
    size: 130,
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return value !== null ? value.toFixed(3) : '--';
    },
    meta: { align: 'right' },
  },
  {
    accessorKey: 'power',
    header: 'Power',
    size: 130,
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return value !== null ? value.toFixed(2) : '--';
    },
    meta: { align: 'right' },
  },
  {
    accessorKey: 'current',
    header: 'Current',
    size: 130,
    cell: ({ getValue }) => getValue() || '--',
    meta: { align: 'right' },
  },
  {
    accessorKey: 'voltage',
    header: 'Voltage',
    size: 130,
    cell: ({ getValue }) => getValue() || '--',
    meta: { align: 'right' },
  },
  {
    accessorKey: 'temperature',
    header: 'Temperature',
    size: 140,
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return value !== null ? `${value.toFixed(1)}°` : '--';
    },
    meta: { align: 'right' },
  },
  {
    accessorKey: 'stateOfCharge',
    header: 'SoC (%)',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return value !== null ? `${value.toFixed(0)}%` : '--';
    },
    meta: { align: 'right' },
  },
  {
    accessorKey: 'tariffName',
    header: 'Tariff',
    size: 160,
    cell: ({ getValue }) => getValue() || '--',
    meta: { align: 'left' },
  },
  {
    accessorKey: 'unitCost',
    header: 'Unit Cost',
    size: 120,
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return value !== null ? value.toFixed(4) : '--';
    },
    meta: { align: 'right' },
  },
  {
    accessorKey: 'costExclVat',
    header: 'Cost (excl VAT)',
    size: 130,
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return value !== null ? value.toFixed(2) : '--';
    },
    meta: { align: 'right' },
  },
  {
    accessorKey: 'vatCost',
    header: 'VAT',
    size: 100,
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return value !== null ? value.toFixed(2) : '--';
    },
    meta: { align: 'right' },
  },
  {
    accessorKey: 'costInclVat',
    header: 'Total (incl VAT)',
    size: 130,
    cell: ({ getValue }) => {
      const value = getValue() as number | null;
      return value !== null ? value.toFixed(2) : '--';
    },
    meta: { align: 'right' },
  },
];

/**
 * Loading skeleton for the grid
 */
function GridLoadingSkeleton({ rowCount = 10 }: { rowCount?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rowCount }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/**
 * Empty state for the grid
 */
function GridEmptyState() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <p className="text-gray-500 font-medium">No Meter Values</p>
        <p className="text-sm text-gray-400 mt-1">
          No meter values found for this session.
        </p>
      </div>
    </div>
  );
}

/**
 * Error state for the grid
 */
function GridErrorState({
  error,
  onRetry,
}: {
  error: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="text-center">
        <p className="text-red-600 font-medium">Failed to Load Meter Values</p>
        <p className="text-sm text-gray-500 mt-1">{error}</p>
        {onRetry && (
          <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
            Try Again
          </Button>
        )}
      </div>
    </div>
  );
}

export function MeterValuesGrid({
  data,
  isLoading,
  error,
  totalCount,
  currentPage,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  unitMode = 'kW',
}: MeterValuesGridProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'timestamp', desc: true },
  ]);

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? currentPage * pageSize + 1 : 0;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  const orderedColumns = useMemo(() => {
    if (unitMode !== 'A') return columns;
    const reordered = [...columns];
    const powerIdx = reordered.findIndex((c) => (c as { accessorKey?: string }).accessorKey === 'power');
    const currentIdx = reordered.findIndex((c) => (c as { accessorKey?: string }).accessorKey === 'current');
    if (powerIdx < 0 || currentIdx < 0) return columns;
    [reordered[powerIdx], reordered[currentIdx]] = [reordered[currentIdx], reordered[powerIdx]];
    return reordered;
  }, [unitMode]);

  const table = useReactTable({
    data,
    columns: orderedColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    getRowId: (row) => row.meterValueId.toString(),
    state: {
      sorting,
    },
  });

  // Generate page numbers
  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;

    let startPage = Math.max(0, currentPage - 1);
    const endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Handle loading state
  if (isLoading && data.length === 0) {
    return <GridLoadingSkeleton />;
  }

  // Handle error state
  if (error) {
    return <GridErrorState error={error} onRetry={onRefresh} />;
  }

  // Handle empty state
  if (!data || data.length === 0) {
    return <GridEmptyState />;
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Table container - using raw HTML table elements for sticky header support */}
      <div
        className={cn('border-x border-t bg-white flex-1 min-h-0', GRID_STYLES.border, isLoading && 'opacity-50')}
        style={{ overflow: 'scroll' }}
      >
        <table className="w-full table-auto caption-bottom text-sm border-separate border-spacing-0">
          <thead className={cn('sticky top-0 z-10 [&_tr]:border-b', GRID_STYLES.header.background)}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className={cn(GRID_STYLES.header.background, 'border-b transition-colors')}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      'h-12 px-4 text-left align-middle text-xs font-medium whitespace-nowrap',
                      GRID_STYLES.header.text,
                      (header.column.columnDef.meta as { align?: string })?.align === 'right' ? 'text-right' : ''
                    )}
                    style={{
                      width: header.getSize(),
                      minWidth: header.getSize(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={cn(
                          'flex items-center gap-1',
                          header.column.getCanSort() && `cursor-pointer select-none ${GRID_STYLES.header.hoverText}`,
                          (header.column.columnDef.meta as { align?: string })?.align === 'right' ? 'justify-end' : ''
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>
                        {header.column.getCanSort() && (
                          <div className="flex flex-col">
                            <ChevronUp className={cn('h-3 w-3', header.column.getIsSorted() === 'asc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive)} />
                            <ChevronDown className={cn('h-3 w-3 -mt-1', header.column.getIsSorted() === 'desc' ? GRID_STYLES.sort.active : GRID_STYLES.sort.inactive)} />
                          </div>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className="[&_tr:last-child]:border-0">
            {table.getRowModel().rows.map((row) => {
              const meterValue = row.original;
              // Virtual meter rows get tan background (matching Flutter: 0xFFF7F1ED)
              const isVirtual = meterValue.isVirtualMeter;
              // Highlight TransactionBegin and TransactionEnd rows in yellow
              const isTransactionBoundary =
                meterValue.readingContext === 'Transaction.Begin' ||
                meterValue.readingContext === 'Transaction.End';

              return (
                <tr
                  key={row.id}
                  className={cn(
                    'border-b transition-colors',
                    GRID_STYLES.border,
                    isTransactionBoundary
                      ? 'bg-yellow-50 hover:bg-yellow-100'
                      : isVirtual
                        ? 'bg-[#F7F1ED] hover:bg-[#EDE7E3]'
                        : `${GRID_STYLES.row.default} ${GRID_STYLES.row.hover}`
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn(
                        'p-4 align-middle py-2 text-sm whitespace-nowrap',
                        (cell.column.columnDef.meta as { align?: string })?.align === 'right' ? 'text-right' : ''
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className={cn('flex-shrink-0 flex items-center justify-between px-2 py-3 border-x border-b bg-white', GRID_STYLES.border)}>
        {/* Page Navigation */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(0)}
            disabled={currentPage === 0 || isLoading}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0 || isLoading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? 'default' : 'ghost'}
              size="sm"
              className={`h-8 w-8 p-0 ${
                currentPage === pageNum
                  ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]'
                  : 'text-[#262626] hover:bg-[#FFFBEB]'
              }`}
              onClick={() => onPageChange(pageNum)}
              disabled={isLoading}
            >
              {pageNum + 1}
            </Button>
          ))}

          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1 || isLoading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]"
            onClick={() => onPageChange(totalPages - 1)}
            disabled={currentPage >= totalPages - 1 || isLoading}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>

          <span className="text-sm text-[#606060] ml-2">
            {currentPage + 1} of {totalPages || 1} pages
          </span>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#606060]">
            {startItem}-{endItem} of {totalCount.toLocaleString()}
          </span>

          <div className="flex items-center gap-2">
            <span className="text-sm text-[#606060]">Rows per page:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8 w-[70px] border-[#E8E8E8] text-[#262626]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={size.toString()} className="hover:bg-[#FFFBEB]">
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}
